"use client";

import { useEffect, useRef } from "react";

export interface RowTransition<Row> {
  id: string;
  prev: string;
  next: string;
  row: Row;
}

export interface UseRowTransitionsOptions<Row> {
  /** Live rows from useTable(...), already filtered by the consumer. */
  rows: readonly Row[];
  /** Table snapshot readiness from useTable(...). */
  isReady: boolean;
  getId: (row: Row) => string;
  getState: (row: Row) => string;
  /**
   * Scope key (e.g. current organization id). A change discards the
   * baseline and re-records silently; null disables the watcher entirely.
   */
  resetKey: string | null;
  onTransition?: (change: RowTransition<Row>) => void;
  /**
   * Opt-in: fires for an id that appears AFTER the baseline and was never
   * seen before within the current resetKey scope. Ids present at baseline
   * (or re-recorded after a reconnect rebaseline) stay silent, and an id
   * that vanishes transiently and refills does NOT re-fire — a per-scope
   * seen-set outlives the row-diff baseline precisely so resubscribe
   * refills can't replay creations.
   */
  onAppear?: (row: Row) => void;
}

/**
 * Generic live-change watcher over a useTable row set. Fires
 * `onTransition` ONLY when a previously-recorded row's state changes:
 * - first ready render (and every resetKey change) records silently, so
 *   initial snapshots and org switches never fire;
 * - ids appearing after the baseline are recorded silently (creation is
 *   not a transition) — unless the consumer opts into `onAppear`, which
 *   fires once per never-before-seen id in the current scope;
 * - ids disappearing are dropped silently — reconnects/resubscribes empty
 *   row sets transiently, and hard deletes are indistinguishable from that
 *   client-side, so disappearance is deliberately NOT a signal;
 * - !isReady clears the baseline, so a reconnect rebaselines instead of
 *   firing on the refill.
 * Callback and selectors are kept in refs — consumers may pass inline
 * closures without effect churn.
 */
export function useRowTransitions<Row>({
  rows,
  isReady,
  getId,
  getState,
  resetKey,
  onTransition,
  onAppear,
}: UseRowTransitionsOptions<Row>): void {
  const baseline = useRef<Map<string, string> | null>(null);
  const baselineKey = useRef<string | null>(null);
  // Every id observed within the current resetKey scope, INCLUDING ids that
  // later vanish from `rows`. Unlike `baseline`, this survives !isReady
  // windows and transient empty row sets, so onAppear can't re-fire when a
  // reconnect/resubscribe refills previously-seen rows.
  const seenIds = useRef<Set<string>>(new Set());
  const seenKey = useRef<string | null>(null);
  const callbacks = useRef({ getId, getState, onTransition, onAppear });
  callbacks.current = { getId, getState, onTransition, onAppear };

  useEffect(() => {
    if (!isReady || resetKey === null) {
      baseline.current = null;
      baselineKey.current = null;
      return;
    }
    const { getId, getState, onTransition, onAppear } = callbacks.current;
    if (seenKey.current !== resetKey) {
      seenIds.current = new Set();
      seenKey.current = resetKey;
    }
    const next = new Map<string, string>();
    for (const row of rows) {
      next.set(getId(row), getState(row));
    }
    if (baseline.current === null || baselineKey.current !== resetKey) {
      baseline.current = next;
      baselineKey.current = resetKey;
      for (const id of next.keys()) seenIds.current.add(id);
      return;
    }
    for (const row of rows) {
      const id = getId(row);
      const prev = baseline.current.get(id);
      const state = next.get(id)!;
      if (prev === undefined) {
        if (!seenIds.current.has(id)) {
          seenIds.current.add(id);
          onAppear?.(row);
        }
      } else if (prev !== state) {
        onTransition?.({ id, prev, next: state, row });
      }
    }
    baseline.current = next; // vanished ids drop out silently
  }, [rows, isReady, resetKey]);
}
